import { zodResolver } from '@hookform/resolvers/zod'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { X } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { z } from 'zod'

import type { Category } from '@ankor-io/common/types/transaction/category'

import { useUserContext } from '@/providers/user/useUserContext'
import { Button } from '@/ui-components/Button'
import { Dialog, DialogActions, DialogTitle } from '@/ui-components/Dialog'
import { ErrorMessage, Field, Label } from '@/ui-components/Fieldset'
import { Input } from '@/ui-components/Input'
import EmojiPicker from '@/views/components/EmojiPicker'
import { MinimalColourPicker } from '@/views/components/MinimalColourPicker'

// removed Select in favor of a compact emoji picker

type AddCategoryModalProps = {
  isOpen: boolean
  onClose: () => void
  onNewCategory: (category: Category) => void
}

const formSchema = z.object({
  name: z.string().min(1, { message: 'Category name is required' }),
  color: z.string({ required_error: 'Colour is required' }).min(1, { message: 'Colour is required' }),
  icon: z.string().optional(),
})
type FormData = z.infer<typeof formSchema>

const AddCategoryModal = ({ isOpen, onClose, onNewCategory }: AddCategoryModalProps) => {
  const { getAccessToken } = useKindeAuth()
  const { activeOrganizationId } = useUserContext()

  const [isSaving, setIsSaving] = useState(false)
  // Form state replaces local field state

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      color: '#64748b',
      icon: '',
    },
  })
  const categoryColour = form.watch('color')
  const categoryIcon = form.watch('icon') ?? ''

  const onSubmit = (data: FormData) => {
    const addCategory = async () => {
      if (!activeOrganizationId) {
        throw new Error('Missing organization context')
      }

      setIsSaving(true)

      try {
        const res = await fetch(`/api/pay/org/${activeOrganizationId}/category`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await getAccessToken()}`,
          },
          body: JSON.stringify({ name: data.name, color: data.color, icon: data.icon }),
        })

        if (!res.ok) {
          throw new Error('Failed to add category')
        }

        const category = await res.json()
        onNewCategory(category)
      } catch (error) {
        console.error('Failed to add category:', error)
        throw new Error('Failed to add category')
      } finally {
        // Close modal and reset form
        onClose()
        setIsSaving(false)
        form.reset()
      }
    }

    toast.promise(addCategory, {
      loading: 'Adding category...',
      success: 'Category added successfully',
      error: 'Failed to add category',
    })
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
    >
      <DialogTitle>
        <div className="flex justify-between items-center">
          <span>Add category</span>
          <Button
            plain
            onClick={onClose}
            aria-label="Close"
          >
            <X data-slot="icon" />
          </Button>
        </div>
      </DialogTitle>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4"
      >
        <Field>
          <Label>Category name</Label>
          <Input
            placeholder="Enter category name"
            value={form.watch('name')}
            onChange={(e) => form.setValue('name', e.target.value, { shouldValidate: true })}
          />
          {form.formState.errors.name && <ErrorMessage>{form.formState.errors.name.message}</ErrorMessage>}
        </Field>

        {/* Colour picker from preset options */}
        <Field>
          <Label>Colour</Label>
          <div className="flex flex-wrap gap-2 mt-3 w-56">
            <MinimalColourPicker
              categoryColour={categoryColour}
              setCategoryColour={(hex) => form.setValue('color', hex, { shouldValidate: true })}
            />
          </div>
          {form.formState.errors.color && <ErrorMessage>{form.formState.errors.color.message}</ErrorMessage>}
        </Field>

        {/* Emoji picker using Dropdown grid */}
        <Field className="flex flex-col gap-3">
          <Label>Icon (optional)</Label>
          <EmojiPicker
            categoryIcon={categoryIcon}
            setEmoji={(emoji) => form.setValue('icon', emoji)}
          />
          {form.formState.errors.icon && <ErrorMessage>{form.formState.errors.icon.message}</ErrorMessage>}
        </Field>

        <DialogActions>
          <Button
            type="submit"
            color="primary"
            className="disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSaving}
          >
            Confirm
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default AddCategoryModal
